import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// Marks the spare history entry as ours, so releasing it cannot pop an entry
// somebody else put there.
const GUARD_STATE = { mssUnsavedChangesGuard: true };

const isGuardEntry = () => !!window.history.state?.mssUnsavedChangesGuard;

/**
 * Keeps work that only exists on the page from disappearing when the learner
 * leaves it: an editor's unsaved edits, a quiz's unsubmitted answers.
 *
 * Three ways out of a page, each stopped the same way:
 *   - closing or reloading the tab, which the browser asks about itself;
 *   - a breadcrumb, which the page routes through `guardNavigation`;
 *   - Back, which is the awkward one. `beforeunload` says nothing about a move
 *     within the app, and React Router only offers `useBlocker` to a data
 *     router, which this app does not use. So while there is something to lose
 *     a spare history entry sits on top of the page's own: Back lands on that
 *     instead of leaving, and the page puts it straight back and asks. The URL
 *     never changes, so nothing is rendered differently while this happens.
 *
 * The spare entry is taken back off the stack as soon as there is nothing left
 * to lose — a quiz that has been handed in, say — so Back is not quietly
 * swallowed afterwards. Leaving through the prompt replaces it rather than
 * pushing past it, for the same reason.
 *
 * @param {Object} params
 * @param {Boolean} params.isDirty Whether the page is holding unsaved work.
 * @param {String} params.backPath Where Back should land, once confirmed.
 * @returns {Object} { isPromptOpen, guardNavigation, navigateAway, stay, leave }
 */
const useUnsavedChangesGuard = ({ isDirty, backPath }) => {
  const navigate = useNavigate();
  const [isPromptOpen, setIsPromptOpen] = useState(false);
  const [pendingPath, setPendingPath] = useState(null);
  const hasSpareEntryRef = useRef(false);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!isDirty) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  useEffect(() => {
    if (!isDirty) return undefined;

    window.history.pushState(GUARD_STATE, '');
    hasSpareEntryRef.current = true;

    const handlePopState = () => {
      // Back has just spent the spare entry. Put another one up so the page
      // still has something to give the next press, and ask about this one.
      window.history.pushState(GUARD_STATE, '');
      setPendingPath(backPath);
      setIsPromptOpen(true);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isDirty, backPath]);

  /*
   * Nothing left to lose while the learner is still here — a quiz submitted in
   * place, or edits undone by hand. Written as its own effect rather than as
   * the cleanup above so that it runs on that change alone: a cleanup also runs
   * when the page unmounts, where popping the stack would fight the navigation
   * that is already under way.
   */
  useEffect(() => {
    if (isDirty || !hasSpareEntryRef.current) return;
    hasSpareEntryRef.current = false;
    if (isGuardEntry()) window.history.back();
  }, [isDirty]);

  /** Follows a link, or asks first if there is unsaved work behind it. */
  const guardNavigation = (path) => {
    if (!isDirty) {
      navigate(path, { relative: 'path' });
      return;
    }
    setPendingPath(path);
    setIsPromptOpen(true);
  };

  /*
   * The page's own way out — Save, Delete — where the learner has already said
   * what should happen and there is nothing to ask about. Replaces the spare
   * entry rather than pushing past it, so Back from wherever this lands does
   * not stop at the page just left.
   */
  const navigateAway = (path) => {
    const replace = isGuardEntry();
    hasSpareEntryRef.current = false;
    navigate(path, { relative: 'path', replace });
  };

  const stay = () => setIsPromptOpen(false);

  const leave = () => {
    setIsPromptOpen(false);
    if (!pendingPath) return;
    navigateAway(pendingPath);
  };

  return { isPromptOpen, guardNavigation, navigateAway, stay, leave };
};

export default useUnsavedChangesGuard;
